import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Camera, Check, Download, Loader2 } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

const PurchaseSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("loading");
  const [purchase, setPurchase] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  const pollPaymentStatus = async () => {
    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      
      if (response.data.payment_status === "paid") {
        setStatus("success");
        // Fetch purchase details
        // For now, we'll just show success
      } else if (response.data.status === "expired") {
        setStatus("expired");
      } else if (pollCount < 10) {
        // Continue polling
        setTimeout(() => {
          setPollCount(prev => prev + 1);
          pollPaymentStatus();
        }, 2000);
      } else {
        setStatus("pending");
      }
    } catch (error) {
      console.error("Payment status error:", error);
      if (pollCount < 5) {
        setTimeout(() => {
          setPollCount(prev => prev + 1);
          pollPaymentStatus();
        }, 2000);
      } else {
        setStatus("error");
      }
    }
  };

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus("error");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="min-h-screen py-12 px-6 flex items-center justify-center" data-testid="purchase-success">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-6">
            <Camera className="w-12 h-12 text-primary" />
          </div>
        </div>

        <Card className="bg-card border-border">
          <CardContent className="p-8 text-center">
            {status === "loading" && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <Loader2 className="w-16 h-16 mx-auto mb-6 text-primary animate-spin" />
                <h2 className="text-2xl font-serif mb-2">Processing Payment</h2>
                <p className="text-muted-foreground">Please wait while we confirm your purchase...</p>
              </motion.div>
            )}

            {status === "success" && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
              >
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Check className="w-8 h-8 text-green-400" />
                </div>
                <h2 className="text-2xl font-serif mb-2">Purchase Complete!</h2>
                <p className="text-muted-foreground mb-6">
                  Thank you for your purchase. You should receive a download link via email shortly.
                </p>
                <div className="space-y-3">
                  <Link to="/galleries">
                    <Button className="w-full btn-primary rounded-sm">
                      Browse More Photos
                    </Button>
                  </Link>
                  <Link to="/">
                    <Button variant="outline" className="w-full">
                      Go Home
                    </Button>
                  </Link>
                </div>
              </motion.div>
            )}

            {status === "pending" && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <Loader2 className="w-16 h-16 mx-auto mb-6 text-yellow-400 animate-spin" />
                <h2 className="text-2xl font-serif mb-2">Payment Processing</h2>
                <p className="text-muted-foreground mb-6">
                  Your payment is being processed. You'll receive an email confirmation once complete.
                </p>
                <Link to="/galleries">
                  <Button className="w-full">
                    Continue Browsing
                  </Button>
                </Link>
              </motion.div>
            )}

            {status === "expired" && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-red-400 text-2xl">!</span>
                </div>
                <h2 className="text-2xl font-serif mb-2">Session Expired</h2>
                <p className="text-muted-foreground mb-6">
                  Your payment session has expired. Please try again.
                </p>
                <Link to="/galleries">
                  <Button className="w-full">
                    Browse Galleries
                  </Button>
                </Link>
              </motion.div>
            )}

            {status === "error" && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-red-400 text-2xl">!</span>
                </div>
                <h2 className="text-2xl font-serif mb-2">Something Went Wrong</h2>
                <p className="text-muted-foreground mb-6">
                  We couldn't verify your payment. If you were charged, please contact support.
                </p>
                <Link to="/galleries">
                  <Button className="w-full">
                    Try Again
                  </Button>
                </Link>
              </motion.div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PurchaseSuccess;
